import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../App';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import SuperAdminLayout from '../../components/SuperAdminLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { Megaphone, Save, Eye, EyeOff, Info, AlertTriangle, CheckCircle } from 'lucide-react';

// Helper to get auth config for axios requests
const getAuthConfig = () => {
  const token = localStorage.getItem('session_token');
  return {
    withCredentials: true,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  };
};

const ANNOUNCEMENT_TYPES = [
  { id: 'info', name: 'Info', icon: Info, color: 'blue', bgClass: 'bg-blue-50 border-blue-200', textClass: 'text-blue-900' },
  { id: 'warning', name: 'Warning', icon: AlertTriangle, color: 'amber', bgClass: 'bg-amber-50 border-amber-200', textClass: 'text-amber-900' },
  { id: 'success', name: 'Success', icon: CheckCircle, color: 'emerald', bgClass: 'bg-emerald-50 border-emerald-200', textClass: 'text-emerald-900' },
];

export default function SuperAdminAnnouncements() {
  const { user, API } = useContext(AppContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [showAnnouncement, setShowAnnouncement] = useState(false);
  const [announcementTitle, setAnnouncementTitle] = useState('Platform Announcement');
  const [announcementContent, setAnnouncementContent] = useState('');
  const [announcementType, setAnnouncementType] = useState('info');
  const [announcementUpdatedAt, setAnnouncementUpdatedAt] = useState(null);

  useEffect(() => {
    if (!user?.is_super_admin) {
      navigate('/home');
      return;
    }
    loadSettings();
  }, [user, navigate]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/platform/settings`, getAuthConfig());
      const data = response.data;
      
      setShowAnnouncement(data.show_announcement || false);
      setAnnouncementTitle(data.announcement_title || 'Platform Announcement');
      setAnnouncementContent(data.announcement_content || '');
      setAnnouncementType(data.announcement_type || 'info');
      setAnnouncementUpdatedAt(data.announcement_updated_at);
    } catch (error) {
      console.error('Failed to load settings:', error);
      toast.error('Failed to load announcement settings');
    } finally {
      setLoading(false);
    }
  };

  const saveAnnouncement = async () => {
    try {
      setSaving(true);
      await axios.patch(
        `${API}/platform/settings`,
        {
          show_announcement: showAnnouncement,
          announcement_title: announcementTitle,
          announcement_content: announcementContent,
          announcement_type: announcementType
        },
        getAuthConfig()
      );
      toast.success('Announcement saved successfully!');
      loadSettings(); // Reload to get updated timestamp
    } catch (error) {
      console.error('Failed to save announcement:', error);
      toast.error('Failed to save announcement');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return null;
    const date = new Date(isoString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const currentTypeConfig = ANNOUNCEMENT_TYPES.find(t => t.id === announcementType) || ANNOUNCEMENT_TYPES[0];
  const TypeIcon = currentTypeConfig.icon;

  if (loading) {
    return (
      <SuperAdminLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
        </div>
      </SuperAdminLayout>
    );
  }

  return (
    <SuperAdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-zinc-100 flex items-center gap-3">
              <Megaphone className="w-7 h-7 text-amber-500" />
              Platform Announcements
            </h1>
            <p className="text-zinc-400 mt-1">
              Display a banner message to all users across the platform
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Settings Card */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-zinc-100">Announcement Settings</CardTitle>
              <CardDescription className="text-zinc-400">
                Configure your platform-wide announcement
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Toggle Visibility */}
              <div className="flex items-center justify-between p-4 bg-zinc-800 rounded-lg">
                <div className="flex items-center gap-3">
                  {showAnnouncement ? (
                    <Eye className="w-5 h-5 text-emerald-500" />
                  ) : (
                    <EyeOff className="w-5 h-5 text-zinc-500" />
                  )}
                  <div>
                    <div className="font-medium text-zinc-100">Show Announcement</div>
                    <p className="text-sm text-zinc-400">
                      {showAnnouncement ? 'Visible to all users' : 'Hidden from users'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowAnnouncement(!showAnnouncement)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    showAnnouncement ? 'bg-emerald-500' : 'bg-zinc-600'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      showAnnouncement ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {/* Announcement Type */}
              <div>
                <Label className="text-zinc-300">Announcement Type</Label>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {ANNOUNCEMENT_TYPES.map(type => {
                    const Icon = type.icon;
                    const isSelected = announcementType === type.id;
                    return (
                      <button
                        key={type.id}
                        onClick={() => setAnnouncementType(type.id)}
                        className={`flex items-center justify-center gap-2 p-3 rounded-lg border-2 transition-all ${
                          isSelected
                            ? `border-${type.color}-500 bg-${type.color}-500/20`
                            : 'border-zinc-700 bg-zinc-800 hover:border-zinc-600'
                        }`}
                      >
                        <Icon className={`w-4 h-4 ${isSelected ? `text-${type.color}-400` : 'text-zinc-400'}`} />
                        <span className={`text-sm font-medium ${isSelected ? `text-${type.color}-400` : 'text-zinc-400'}`}>
                          {type.name}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Title */}
              <div>
                <Label className="text-zinc-300">Title</Label>
                <Input
                  value={announcementTitle}
                  onChange={(e) => setAnnouncementTitle(e.target.value)}
                  placeholder="Platform Announcement"
                  className="mt-2 bg-zinc-800 border-zinc-700 text-zinc-100"
                />
              </div>

              {/* Content */}
              <div>
                <Label className="text-zinc-300">Content</Label>
                <Textarea
                  value={announcementContent}
                  onChange={(e) => setAnnouncementContent(e.target.value)}
                  placeholder="Enter your announcement message...

Examples:
• Season 50 Draft begins this Friday at 8 PM EST!
• Maintenance scheduled for Sunday 2 AM - 4 AM EST
• New feature: SMS notifications are now available"
                  rows={8}
                  className="mt-2 bg-zinc-800 border-zinc-700 text-zinc-100 font-mono text-sm"
                />
                <p className="text-xs text-zinc-500 mt-1">
                  Supports line breaks. Use bullet points (•) for lists.
                </p>
              </div>

              {/* Save Button */}
              <Button 
                onClick={saveAnnouncement} 
                disabled={saving}
                className="w-full bg-amber-500 hover:bg-amber-600 text-zinc-900"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-zinc-900 mr-2"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Announcement
                  </>
                )}
              </Button>

              {announcementUpdatedAt && (
                <p className="text-xs text-zinc-500 text-center">
                  Last updated: {formatDate(announcementUpdatedAt)}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Preview Card */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-zinc-100">Preview</CardTitle>
              <CardDescription className="text-zinc-400">
                How the announcement will appear on users' Home screens
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="bg-slate-100 p-6 rounded-lg min-h-[300px]">
                {showAnnouncement && announcementContent ? (
                  <Card className={`${currentTypeConfig.bgClass} border`}>
                    <CardHeader className="pb-2">
                      <CardTitle className={`${currentTypeConfig.textClass} flex items-center gap-2 text-lg`}>
                        <TypeIcon className="w-5 h-5" />
                        {announcementTitle || 'Platform Announcement'}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-slate-700 whitespace-pre-wrap text-sm">
                        {announcementContent}
                      </div>
                      {announcementUpdatedAt && (
                        <p className="text-xs text-slate-500 mt-3">
                          Posted: {formatDate(announcementUpdatedAt)}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full min-h-[200px] text-slate-400">
                    <EyeOff className="w-12 h-12 mb-3 opacity-50" />
                    <p className="text-center">
                      {!showAnnouncement 
                        ? 'Announcement is currently hidden' 
                        : 'No content to display'}
                    </p>
                    <p className="text-sm mt-1 text-slate-500">
                      Toggle visibility and add content to see preview
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </SuperAdminLayout>
  );
}
